import { useEffect, useState } from 'react';
import { HttpStatusCode } from 'axios';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import useApi from '../../../../../../../utils/AxiosWrapper';
import { formatNumberCommas } from '../../../../../../../utils/CurrencyUtils';
import { OwnershipStatus } from '../../../../../../../types/apiTypes';
import { FLEET_MODE_ASSET_INSURANCE_PAYMENT } from '../fleet-insurance/fleetInsurance';
import {
  AssetFormData,
  AssetFormMode,
  AssetType,
  AssetView,
  FleetInsuranceAware,
  OutOfServiceReason,
} from './types';

/**
 * The stored `ownership_status` is a free STRING column, so an asset created
 * before the `OwnershipStatus` enum — or through the old edit dropdown, which
 * offered `leaseToOwn`/`contracted` — can hold a value the API will no longer
 * accept. Seeding that value straight into the form left the user with a save
 * that 400s every time and shows nothing (NODE-AWSLAMBDA-X0), so an
 * unrecognised value is narrowed to `Owned` at seed time: the dropdown then
 * shows exactly what a save would send, instead of the browser silently
 * resolving an option that isn't there.
 *
 * Same shape as the guard `PendingTruckCard` already uses on the approval card.
 */
const toFormOwnershipStatus = (value?: string | null): OwnershipStatus =>
  (Object.values(OwnershipStatus) as string[]).includes(value ?? '')
    ? (value as OwnershipStatus)
    : OwnershipStatus.Owned;

/**
 * Per-mode react-hook-form defaults — copied verbatim from the legacy forms.
 * They differ (create defaults `paysForPMSRepairs: true`; edit defaults it
 * `false` and seeds numeric/mileage fields), so the difference is preserved.
 */
const CREATE_DEFAULTS: Partial<AssetFormData> = {
  assetType: 'truck',
  tireBrandQuality: 'premium',
  status: 'in_commission',
  condition: 'unmodified',
  paysForPMSRepairs: true,
  ownershipStatus: OwnershipStatus.Owned,
};

const EDIT_DEFAULTS: Partial<AssetFormData> = {
  assetType: 'truck',
  tireBrandQuality: 'premium',
  condition: 'unmodified',
  paysForPMSRepairs: false,
  assetPayment: 0,
  insurancePayment: 0,
  typicalAnnualMileage: '0',
  assetMileage: '0',
  ownershipStatus: OwnershipStatus.Owned,
  assetDisplayName: '',
};

type UseAssetFormArgs = FleetInsuranceAware & {
  mode: AssetFormMode;
  /** Edit only — the asset being edited (may be null until the GET resolves). */
  selectedAsset?: AssetView | null;
  setSelectedAsset?: (asset: AssetView) => void;
  /** Edit only — the existing image URL passed down by the parent. */
  assetImageUrl?: string;
  setAssetImageUrl?: (url: string) => void;
};

/**
 * Owns the asset form's react-hook-form instance, the image state, the shared
 * `loading` flag, and (edit only) the prefill that hydrates the form from the
 * existing asset.
 *
 * Edit prefill intentionally keeps the legacy two-path behavior:
 *   1. params present + no selectedAsset yet -> GET `/assets/type/:type/:id`,
 *      then `setValue` from the raw payload (reads `pays_for_repairs`, raw
 *      mileage numbers);
 *   2. selectedAsset already supplied -> `setValue` from it (reads
 *      `pay_for_repairs`, comma-formatted mileage via `formatNumberCommas`).
 * The differing field spellings/formatting are preserved on purpose — see the
 * parity snapshot in docs/spec-assetform-decomposition-ui.md.
 */
export function useAssetForm({
  mode,
  isFleetInsurance,
  selectedAsset,
  setSelectedAsset,
  assetImageUrl,
  setAssetImageUrl,
}: UseAssetFormArgs) {
  const form = useForm<AssetFormData>({
    defaultValues: mode === 'create' ? CREATE_DEFAULTS : EDIT_DEFAULTS,
  });
  const { watch, setValue } = form;
  const { callProtectedApi } = useApi();
  const { asset_type, asset_id } = useParams();

  const [loading, setLoading] = useState(false);

  // ---- Image state ----
  const [assetImage, setAssetImage] = useState<File | null>(null);
  const [imageRemoved, setImageRemoved] = useState(false);
  const [displayImageUrl, setDisplayImageUrl] = useState(assetImageUrl ?? '');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showImage, setShowImage] = useState<boolean>(false);

  const ownershipStatus = watch('ownershipStatus');

  // Create-only: keep `paysForPMSRepairs` true while ownership is "owned"
  // (legacy CreateAsset effect). Edit never auto-toggles this.
  useEffect(() => {
    if (mode === 'create' && ownershipStatus === 'owned') {
      setValue('paysForPMSRepairs', true, { shouldDirty: true });
    }
  }, [mode, ownershipStatus, setValue]);

  // Edit-only: keep displayImageUrl in sync with the parent's URL.
  useEffect(() => {
    if (mode !== 'edit') return;
    if (!imageRemoved && !previewUrl) {
      setDisplayImageUrl(assetImageUrl ?? '');
    }
  }, [mode, assetImageUrl, imageRemoved, previewUrl]);

  // Edit-only: initialize show_asset_image from the selected asset.
  useEffect(() => {
    if (mode !== 'edit' || !selectedAsset) return;
    // Default to false if show_asset_image is undefined (legacy behavior).
    const hasImage = selectedAsset.show_asset_image ?? false;
    setShowImage(hasImage);
    if (!hasImage) {
      setDisplayImageUrl('');
      setImageRemoved(true);
    } else if (assetImageUrl && hasImage) {
      setDisplayImageUrl(assetImageUrl);
      setImageRemoved(false);
    }
  }, [mode, selectedAsset, assetImageUrl]);

  // Edit-only: revoke object URLs to avoid leaks.
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    setAssetImage(file);
    if (mode === 'edit') {
      setImageRemoved(false); // Clear removal flag when a new image is selected
      setShowImage(true); // Show image when a new one is uploaded
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      const newPreviewUrl = URL.createObjectURL(file);
      setPreviewUrl(newPreviewUrl);
      setDisplayImageUrl(newPreviewUrl);
    }
  };

  const handleRemoveImage = () => {
    setAssetImage(null);
    setImageRemoved(true);
    setDisplayImageUrl('');
    setShowImage(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  // ---- Edit prefill ----
  useEffect(() => {
    if (mode !== 'edit') return;

    const applyFromPayload = (data: AssetView) => {
      setValue('assetType', data?.asset_type as AssetType);
      setValue('tireBrandQuality', data?.tire_brand_quality || 'premium');
      setValue(
        'condition',
        data?.engine_condition as AssetFormData['condition']
      );
      setValue('paysForPMSRepairs', Boolean(data?.pays_for_repairs));
      setValue('vinNumber', data?.vehicle_id_number ?? '');
      setValue(
        'assetPayment',
        (data?.asset_type === 'truck'
          ? data?.truck_payment_amount
          : data?.trailer_payment_amount) ?? 0
      );
      setValue('insurancePayment', data?.insurance_payment_amount ?? 0);
      // Fetch path stores raw mileage numbers (legacy did not comma-format here).
      setValue(
        'typicalAnnualMileage',
        (data?.typical_annual_mileage ?? '') as unknown as string
      );
      setValue(
        'assetMileage',
        (data?.current_mileage ?? '') as unknown as string
      );
      setValue(
        'status',
        data?.is_currently_out_of_service
          ? 'out_of_commission'
          : 'in_commission'
      );
      setValue('outOfServiceReason', data?.reason as OutOfServiceReason);
      setValue('ownershipStatus', toFormOwnershipStatus(data?.ownership_status));
      setValue('dateOfLastPM', data?.date_of_last_pm ?? '');
      setValue('dateOfLastRepair', data?.date_of_last_repair ?? '');
      setValue(
        'assetDisplayName',
        (data?.asset_type === 'truck'
          ? data?.truck_display_name
          : data?.trailer_display_name) ?? ''
      );
    };

    const fetchAssetDetails = async (id: string, type: AssetType) => {
      setLoading(true);
      try {
        const response = await callProtectedApi(
          `/assets/type/${type}/${id}`,
          'get',
          {},
          `Error occurred while fetching your ${type} asset.`
        );
        const data: AssetView = response?.data?.data?.asset;
        if (
          data &&
          Object.entries(data).length > 0 &&
          response?.status === HttpStatusCode.Ok
        ) {
          setSelectedAsset?.(data);
          applyFromPayload(data);
        }
      } catch (e: any) {
        console.error('Error occurred while fetching your asset: ', e);
      } finally {
        setLoading(false);
      }
    };

    if (asset_type && asset_id && (selectedAsset === null || !selectedAsset)) {
      fetchAssetDetails(asset_id, asset_type as AssetType);
    } else if (selectedAsset) {
      // selectedAsset path: reads `pay_for_repairs` and comma-formats mileage.
      setValue('assetType', selectedAsset?.asset_type as AssetType);
      setValue(
        'tireBrandQuality',
        selectedAsset?.tire_brand_quality || 'premium'
      );
      setValue(
        'condition',
        selectedAsset?.engine_condition as AssetFormData['condition']
      );
      setValue('paysForPMSRepairs', Boolean(selectedAsset?.pay_for_repairs));
      setValue('vinNumber', selectedAsset?.vehicle_id_number ?? '');
      setValue(
        'assetPayment',
        (selectedAsset?.asset_type === 'truck'
          ? selectedAsset?.truck_payment_amount
          : selectedAsset?.trailer_payment_amount) ?? 0
      );
      setValue(
        'insurancePayment',
        selectedAsset?.insurance_payment_amount ?? 0
      );
      setValue(
        'status',
        selectedAsset?.is_currently_out_of_service
          ? 'out_of_commission'
          : 'in_commission'
      );
      setValue(
        'outOfServiceReason',
        selectedAsset?.reason as OutOfServiceReason
      );
      setValue(
        'typicalAnnualMileage',
        formatNumberCommas(selectedAsset?.typical_annual_mileage ?? 0)
      );
      setValue(
        'assetMileage',
        formatNumberCommas(selectedAsset?.current_mileage ?? 0)
      );
      setValue(
        'ownershipStatus',
        toFormOwnershipStatus(selectedAsset?.ownership_status)
      );
      setValue('dateOfLastPM', selectedAsset?.date_of_last_pm ?? '');
      setValue('dateOfLastRepair', selectedAsset?.date_of_last_repair ?? '');
      setValue(
        'assetDisplayName',
        (selectedAsset?.asset_type === 'truck'
          ? selectedAsset?.truck_display_name
          : selectedAsset?.trailer_display_name) ?? ''
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset_type, asset_id]);

  /**
   * THT-675 — in fleet mode the per-asset insurance field is on screen but
   * disabled and blank (§8/R3.2), and every payload still has to carry the key
   * (trucks, trailers, admin-users and bulk assets all keep
   * `insurance_payment_amount` required).
   *
   * What goes in it depends on whether there is anything to lose (John,
   * 2026-08-25):
   *   - CREATE has no stored premium, so 0 is the honest placeholder.
   *   - EDIT must pass the STORED per-asset amount straight through. Zeroing it
   *     here would let an unrelated edit (a name, a mileage) silently wipe the
   *     premium, and AC5's promise — switch back to Each Truck and your old
   *     per-truck amounts are used again — reads those very columns. Fleet mode
   *     only stops SHOWING the number; it never destroys it. This also means a
   *     stale tab left in fleet mode cannot zero live values after the customer
   *     switches back somewhere else.
   *
   * The stored record is the source of truth rather than the form field, which
   * is merely prefilled from it and is not editable while fleet mode is on.
   */
  const toSubmitValues = (data: AssetFormData): AssetFormData => {
    if (!isFleetInsurance) return data;
    if (mode === 'create') {
      return { ...data, insurancePayment: FLEET_MODE_ASSET_INSURANCE_PAYMENT };
    }
    const storedPayment = Number(selectedAsset?.insurance_payment_amount);
    // No resolved asset yet (the GET path can run without `setSelectedAsset`):
    // the form prefill is the same stored value, so leave it untouched.
    return Number.isFinite(storedPayment)
      ? { ...data, insurancePayment: storedPayment }
      : data;
  };

  return {
    form,
    toSubmitValues,
    loading,
    setLoading,
    // image state + handlers
    assetImage,
    setAssetImage,
    imageRemoved,
    displayImageUrl,
    previewUrl,
    showImage,
    handleDrop,
    handleRemoveImage,
    setAssetImageUrl,
  };
}
